/**
 * MusicManager — MP3 music playback, crossfade, volume control.
 * Extracted from SoundManager to keep music logic isolated.
 *
 * Music tracks loaded from /sounds/ folder:
 *   main_menu, workshop, music_level1..5, end_credits
 */

// ── Music track keys ──
export const MusicTrack = {
  MAIN_MENU: 'main_menu',
  MAP_SELECT_1: 'map_selection1',
  MAP_SELECT_2: 'map_selection2',
  WORKSHOP: 'workshop',
  LEVEL_1: 'music_level1',
  LEVEL_2: 'music_level2',
  LEVEL_3: 'music_level3',
  LEVEL_4: 'music_level4',
  LEVEL_5: 'music_level5',
  END_CREDITS: 'end_credits',
} as const;
export type MusicTrackKey = (typeof MusicTrack)[keyof typeof MusicTrack];

// All level tracks for random selection
const LEVEL_TRACKS: MusicTrackKey[] = [
  MusicTrack.LEVEL_1, MusicTrack.LEVEL_2, MusicTrack.LEVEL_3,
  MusicTrack.LEVEL_4, MusicTrack.LEVEL_5,
];

const MAP_SELECT_TRACKS: MusicTrackKey[] = [
  MusicTrack.MAP_SELECT_1, MusicTrack.MAP_SELECT_2,
];

/**
 * Handles all music loading, playback, volume, and autoplay-unlock logic.
 * Instantiated internally by SoundManager — not a public singleton.
 */
export class MusicManager {
  private musicElements: Map<string, HTMLAudioElement> = new Map();
  private currentMusic: HTMLAudioElement | null = null;
  private currentMusicKey: string = '';
  private pendingMusicKey: MusicTrackKey | '' = '';
  private autoplayUnlocked = false;
  private gestureListenerAdded = false;

  /** Owned by SoundManager — MusicManager reads these via getter closures */
  private getMusicVolume: () => number;
  private isMuted: () => boolean;

  constructor(getMusicVolume: () => number, isMuted: () => boolean) {
    this.getMusicVolume = getMusicVolume;
    this.isMuted = isMuted;
  }

  // ── Loading ──

  loadMusicTracks(): void {
    const tracks = [
      MusicTrack.MAIN_MENU,
      ...MAP_SELECT_TRACKS,
      MusicTrack.WORKSHOP,
      ...LEVEL_TRACKS,
      MusicTrack.END_CREDITS,
    ];
    for (const key of tracks) {
      const audio = new Audio(`/sounds/${key}.mp3`);
      audio.preload = 'auto';
      audio.loop = true;
      this.musicElements.set(key, audio);
    }
  }

  // ── Playback ──

  playMusic(key: MusicTrackKey): void {
    if (this.currentMusicKey === key && this.currentMusic && !this.currentMusic.paused) return;
    this.stopMusic();
    const el = this.musicElements.get(key);
    if (!el) return;
    el.currentTime = 0;
    el.volume = this.isMuted() ? 0 : this.getMusicVolume();
    el.loop = true;
    el.play()
      .then(() => {
        this.autoplayUnlocked = true;
        this.pendingMusicKey = '';
      })
      .catch(() => {
        // Autoplay blocked — store pending key and retry on first user gesture
        this.pendingMusicKey = key;
        if (!this.autoplayUnlocked) {
          this.listenForUserGesture();
        }
      });
    this.currentMusic = el;
    this.currentMusicKey = key;
  }

  /** Register a one-time listener for user gesture to unlock audio autoplay */
  private listenForUserGesture(): void {
    if (this.gestureListenerAdded) return;
    this.gestureListenerAdded = true;

    const unlock = () => {
      this.autoplayUnlocked = true;
      if (this.pendingMusicKey) {
        this.playMusic(this.pendingMusicKey as MusicTrackKey);
      }
      document.removeEventListener('pointerdown', unlock);
      document.removeEventListener('keydown', unlock);
    };
    document.addEventListener('pointerdown', unlock, { once: false });
    document.addEventListener('keydown', unlock, { once: false });
  }

  playRandomLevelMusic(): void {
    const track = LEVEL_TRACKS[Math.floor(Math.random() * LEVEL_TRACKS.length)];
    this.playMusic(track);
  }

  /** Play level music cycling by map index: map 1->track 1, ..., map 5->track 5, map 6->track 1, etc. */
  playLevelMusicForMap(mapIndex: number): void {
    const idx = ((mapIndex - 1) % LEVEL_TRACKS.length + LEVEL_TRACKS.length) % LEVEL_TRACKS.length;
    this.playMusic(LEVEL_TRACKS[idx]);
  }

  playRandomMapSelectMusic(): void {
    const track = MAP_SELECT_TRACKS[Math.floor(Math.random() * MAP_SELECT_TRACKS.length)];
    this.playMusic(track);
  }

  getCurrentMusicKey(): string {
    return this.currentMusicKey;
  }

  /** Get current music progress (0-1) and duration in seconds */
  getMusicProgress(): { progress: number; currentTime: number; duration: number } {
    if (!this.currentMusic || this.currentMusic.paused || !this.currentMusic.duration) {
      return { progress: 0, currentTime: 0, duration: 0 };
    }
    return {
      progress: this.currentMusic.currentTime / this.currentMusic.duration,
      currentTime: this.currentMusic.currentTime,
      duration: this.currentMusic.duration,
    };
  }

  /** Seek music to a specific progress (0-1) */
  seekMusic(progress: number): void {
    if (!this.currentMusic || !this.currentMusic.duration) return;
    this.currentMusic.currentTime = Phaser.Math.Clamp(progress, 0, 1) * this.currentMusic.duration;
  }

  stopMusic(): void {
    if (this.currentMusic) {
      this.currentMusic.pause();
      this.currentMusic.currentTime = 0;
      this.currentMusic = null;
      this.currentMusicKey = '';
    }
  }

  /** Update volume on current music element (called when volume/muted changes) */
  applyVolume(): void {
    if (this.currentMusic) {
      this.currentMusic.volume = this.isMuted() ? 0 : this.getMusicVolume();
    }
  }
}
